import { useRef, useState } from "react";

import { cn } from "../ui/cn";
import { LinkIcon } from "../ui/icons";
import { LinkPopover, type LinkDraft } from "./LinkPopover";
import {
  isCommandActive,
  runCommand,
  type Command,
  type RichTextHandle,
} from "./RichText";

/**
 * The formatting half of a toolbar: bold, italic, lists, indent and links.
 *
 * Lifted out of `MessageEditor`, which owned it alone, when the canned-response
 * library needed the same thing. A snippet is written once and sent hundreds of
 * times, so it is the text on this desk that can least afford to be the plain
 * one -- and it goes into the very editor this toolbar already drives, which is
 * what makes a second implementation indefensible: two toolbars over the same
 * `execCommand` surface would differ in which buttons they offered long before
 * they differed in anything that mattered.
 *
 * A fragment rather than a bar of its own, so each caller keeps its own
 * container and puts whatever else it offers -- emoji, snippets, attachments --
 * alongside these in one row. The link popover travels with the buttons because
 * it is the link button's own state and nothing outside has any use for it.
 */
export function FormatToolbar({
  editor,
  onChange,
}: {
  editor: React.RefObject<RichTextHandle | null>;
  /**
   * Called with the editor's HTML after a link is inserted.
   *
   * Only links need it. The other commands run inside the surface, whose own
   * `onInput` reports the change; `execCommand("insertHTML")` through the
   * popover does not always fire one, so the caller is told by hand.
   */
  onChange: (html: string) => void;
}) {
  const [linkDraft, setLinkDraft] = useState<LinkDraft | null>(null);
  const linkRange = useRef<Range | null>(null);

  return (
    <>
      <FormatButton command="bold" label="Bold">
        <span className="font-bold">B</span>
      </FormatButton>
      <FormatButton command="italic" label="Italic">
        <span className="italic">I</span>
      </FormatButton>
      <FormatButton command="underline" label="Underline">
        <span className="underline">U</span>
      </FormatButton>

      <Divider />

      <FormatButton command="insertUnorderedList" label="Bulleted list">
        •—
      </FormatButton>
      <FormatButton command="insertOrderedList" label="Numbered list">
        1.
      </FormatButton>

      {/* Tab and Shift+Tab do the same thing from the keyboard. These are
          here so the feature is findable without knowing that. */}
      <FormatButton command="outdent" label="Decrease indent (Shift+Tab)">
        ⇤
      </FormatButton>
      <FormatButton command="indent" label="Increase indent (Tab)">
        ⇥
      </FormatButton>

      <Divider />

      <button
        type="button"
        title="Insert link"
        aria-label="Insert link"
        // preventDefault on mousedown keeps the editor's selection alive,
        // which is what makes it available to capture here.
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => {
          const selection = window.getSelection();
          const live =
            selection && selection.rangeCount > 0 && !selection.isCollapsed
              ? selection.getRangeAt(0)
              : null;
          linkRange.current = live ? live.cloneRange() : null;

          const rect = event.currentTarget.getBoundingClientRect();
          setLinkDraft({
            top: rect.bottom + 4,
            left: rect.left,
            text: live ? live.toString() : "",
          });
        }}
        className="flex h-7 w-7 items-center justify-center rounded text-muted hover:bg-surface hover:text-brand-600 dark:hover:text-brand-400"
      >
        <LinkIcon />
      </button>

      {linkDraft ? (
        <LinkPopover
          draft={linkDraft}
          onCancel={() => {
            setLinkDraft(null);
            linkRange.current = null;
          }}
          onSubmit={(url, text) => {
            insertLink(editor.current, onChange, linkRange.current, url, text);
            setLinkDraft(null);
            linkRange.current = null;
          }}
        />
      ) : null}
    </>
  );
}

export function Divider() {
  return <span className="mx-0.5 h-4 w-px bg-line" aria-hidden />;
}

function FormatButton({
  command,
  label,
  children,
}: {
  command: Command;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={isCommandActive(command)}
      /*
       * mousedown is prevented so the button never takes focus. Without it the
       * editor loses its selection on the way down and the command applies to
       * nothing.
       */
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => runCommand(command)}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded text-xs text-muted",
        "hover:bg-surface hover:text-brand-600 dark:hover:text-brand-400",
        isCommandActive(command) && "bg-surface text-content",
      )}
    >
      {children}
    </button>
  );
}

/**
 * Wrap the captured selection in a link.
 *
 * The Range is passed in rather than read from the document: the agent has been
 * typing in the popover, so the editor's own selection no longer points at what
 * they meant to link. Restoring it first is what makes execCommand act on the
 * right text.
 */
function insertLink(
  editor: RichTextHandle | null,
  onChange: (html: string) => void,
  range: Range | null,
  url: string,
  text: string,
): void {
  const selection = window.getSelection();

  if (range && selection) {
    // Text was selected when the popover opened: link exactly that.
    editor?.focus();
    selection.removeAllRanges();
    selection.addRange(range);
  } else {
    /*
     * Nothing was selected, so the link goes where the caret was. This used to
     * be a bare focus(), which collapses the caret to the start of the editor
     * -- so a link added at the end of a written reply appeared above it.
     */
    editor?.restoreCaret();
  }

  if (text !== "") {
    // An explicit label replaces whatever was selected, so the same path
    // handles "link this text" and "insert a link here".
    document.execCommand(
      "insertHTML",
      false,
      `<a href="${escapeHtml(url)}">${escapeHtml(text)}</a>`,
    );
  } else if (range && !range.collapsed) {
    // No label given: keep the selection's own markup rather than flattening
    // it to plain text.
    document.execCommand("createLink", false, url);
  } else {
    document.execCommand(
      "insertHTML",
      false,
      `<a href="${escapeHtml(url)}">${escapeHtml(url)}</a>`,
    );
  }

  const host = document.activeElement as HTMLElement | null;
  if (host?.isContentEditable) onChange(host.innerHTML);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
